import { formatInteger } from "./numberFormat";

const STORAGE_KEY = "vektor_prettify_quantities";

let _enabled: boolean | null = null;

function readSetting(): boolean {
  if (_enabled !== null) return _enabled;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    _enabled = stored !== null ? stored === "true" : true;
  } catch {
    _enabled = true;
  }
  return _enabled;
}

export function isPrettifyEnabled(): boolean {
  return readSetting();
}

export function setPrettifyEnabled(enabled: boolean): void {
  _enabled = enabled;
  try {
    localStorage.setItem(STORAGE_KEY, String(enabled));
  } catch {}
}

export function togglePrettify(): boolean {
  const next = !isPrettifyEnabled();
  setPrettifyEnabled(next);
  return next;
}

export function formatQuantity(qty: number, stackSize = 64): string {
  if (!isPrettifyEnabled()) {
    return formatInteger(qty);
  }
  return prettifyQuantity(qty, stackSize);
}

export function prettifyQuantity(qty: number, stackSize = 64): string {
  if (qty <= 0) return "0";

  const dkSize = 54 * stackSize;
  let remaining = qty;
  const parts: string[] = [];

  const dks = Math.floor(remaining / dkSize);
  remaining %= dkSize;
  if (dks > 0) parts.push(`${dks} DK`);

  // A stack size of 1 means the item does not stack at all (tools, armor), so there is no
  // "N stacks of 1" tier to show; the whole remainder is just a count.
  if (stackSize > 1) {
    const stacks = Math.floor(remaining / stackSize);
    remaining %= stackSize;
    if (stacks > 0) parts.push(`${stacks} Stack${stacks > 1 ? "s" : ""}`);
  }

  if (remaining > 0) parts.push(`${remaining}`);

  return parts.join(" + ");
}

export function quantityBreakdown(qty: number, stackSize = 64): string | null {
  if (!isPrettifyEnabled() || qty <= stackSize) return null;
  return prettifyQuantity(qty, stackSize);
}

export function formatQuantityCompact(qty: number, stackSize = 64): string {
  if (!isPrettifyEnabled()) {
    return formatInteger(qty);
  }
  if (qty <= 0) return "0";

  const dkSize = 54 * stackSize;

  if (qty >= dkSize) {
    const count = Math.round(qty / dkSize);
    const exact = count * dkSize === qty;
    return `${exact ? "" : "~"}${count} DK`;
  }

  if (qty >= stackSize && stackSize > 1) {
    const count = Math.round(qty / stackSize);
    const exact = count * stackSize === qty;
    return `${exact ? "" : "~"}${count}x${stackSize}`;
  }

  return String(qty);
}

export function formatQuantityFull(qty: number, stackSize = 64): string {
  if (qty <= stackSize) return String(qty);
  const pretty = prettifyQuantity(qty, stackSize);
  return `${formatInteger(qty)} (${pretty})`;
}
